import i18n from '@/utils/i18n';
import React from 'react';
import { AgentThreadList } from '@/experiences/sales-portal/domain/superintelligence/components/agent-thread-list';
import {
  Button,
  Drawer,
  Icons,
  ScrollView,
  SectionHeading,
  useReducedMotion,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { useAui } from '@assistant-ui/store';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/theme-context';
import { useWindowDimensions } from 'react-native';

const DRAWER_MAX_WIDTH = 324;
const DRAWER_WIDTH_RATIO = 0.8;
const REDUCED_MOTION_DRAWER_ANIMATION = '75ms';
const STANDARD_DRAWER_ANIMATION = 'takeover';
const NEW_CONVERSATION_ICON_SIZE = 16;

interface AgentThreadDrawerProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const AgentThreadDrawer: React.FC<AgentThreadDrawerProps> = ({
  onOpenChange,
  open,
}) => {
  const theme = useTheme();
  const aui = useAui();
  const insets = useSafeAreaInsets();
  const reduceMotion = useReducedMotion();
  const { width: windowWidth } = useWindowDimensions();
  const drawerWidth = Math.min(
    DRAWER_MAX_WIDTH,
    windowWidth * DRAWER_WIDTH_RATIO,
  );
  const close = () => onOpenChange(false);

  return (
    <Drawer onOpenChange={onOpenChange} open={open}>
      <Drawer.Overlay />
      <Drawer.Content
        animation={
          reduceMotion
            ? REDUCED_MOTION_DRAWER_ANIMATION
            : STANDARD_DRAWER_ANIMATION
        }
        paddingVertical={0}
        width={drawerWidth}
      >
        <YStack
          flex={1}
          gap={theme.sk.spacing.spaceSmall}
          paddingBottom={insets.bottom + theme.sk.spacing.spaceMedium}
          paddingHorizontal={theme.sk.spacing.spaceMedium}
          paddingTop={insets.top + theme.sk.spacing.spaceMedium}
          width="100%"
        >
          <SectionHeading
            size="$sm"
            title={i18n.t('sales_portal.superintelligence.conversations')}
          />
          <Button
            icon={
              <Icons.SquarePen
                color={theme.sk.color.icon.primary}
                size={NEW_CONVERSATION_ICON_SIZE}
              />
            }
            onPress={() => {
              aui.threads.switchToNewThread();
              close();
            }}
            size="$sm"
            variant="secondary"
            width="100%"
          >
            {i18n.t('sales_portal.superintelligence.new_conversation')}
          </Button>
          <ScrollView flex={1}>
            <AgentThreadList onSelect={close} />
          </ScrollView>
        </YStack>
      </Drawer.Content>
    </Drawer>
  );
};
